import { DrawProduct, View } from "@novorender/api";
import { vec3 } from "gl-matrix";
import { useEffect, useRef, useState } from "react";

import { PointLine } from "../pointLineSlice";

/**
 * Find draw product for each point line if firstTwoPointAxisMeasurements is present.
 */
export function usePointToPointAxisDrawProduct(view: View | undefined, pointLines: PointLine[]) {
    const [drawProducts, setDrawProducts] = useState<(DrawProduct | undefined)[]>([]);

    const cacheRef = useRef(new WeakMap<PointLine, DrawProduct | undefined>());

    useEffect(() => {
        const abortController = new AbortController();
        getDrawProduct();

        async function getDrawProduct() {
            const measureView = view?.measure;
            if (!measureView) {
                return;
            }

            const maxDistSqr = 0.001 ** 2;
            const promises = pointLines.map(async (line) => {
                if (!line.firstTwoPointAxisMeasurements) {
                    return;
                }

                let value = cacheRef.current.get(line);
                if (!value) {
                    value = await measureView.draw.getDrawEntity(line.firstTwoPointAxisMeasurements);

                    // Keep only ...-axis parts and ensure they are not too close to the original line
                    const parts = value.objects[0].parts;
                    const newParts: typeof parts = [];
                    for (const part of parts) {
                        if (!["x-axis", "y-axis", "z-axis"].includes(part.name!)) {
                            continue;
                        }

                        const [{ position: p1 }, { position: p2 }] = line.points;
                        const [pAxis1, pAxis2] = part.vertices3D;

                        // Points are too close to the original line, so skip it
                        if (
                            (vec3.sqrDist(p1, pAxis1) < maxDistSqr && vec3.sqrDist(p2, pAxis2) < maxDistSqr) ||
                            (vec3.sqrDist(p1, pAxis2) < maxDistSqr && vec3.sqrDist(p2, pAxis1) < maxDistSqr)
                        ) {
                            continue;
                        }

                        newParts.push(part);
                    }

                    value = { ...value, objects: [{ ...value.objects[0], parts: newParts }] };
                    cacheRef.current.set(line, value);
                }

                return value;
            });

            const axisDrawProducts = await Promise.all(promises);
            if (abortController.signal.aborted) {
                return;
            }

            setDrawProducts(axisDrawProducts);
        }

        return () => {
            abortController.abort();
        };
    }, [view, pointLines]);

    return drawProducts;
}
